import { useState } from 'react';
import { FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { Rarity, Sport } from '__generated__/globalTypes';
import { Button } from 'atoms/buttons/Button';
import { Cards } from 'atoms/icons/Cards';
import { Energy } from 'atoms/icons/Energy';
import { Shards } from 'atoms/icons/Shards';
import { Horizontal, Vertical } from 'atoms/layout/flex';
import { LinkBox, LinkOther, LinkOverlay } from 'atoms/navigation/Box';
import { ellipsis } from 'atoms/typography/Ellipsis';
import { Badge } from 'atoms/ui/Badge';
import { RaffleTicketBalanceButton } from 'components/RaffleTicketBalanceButton';
import { CommonCoinsBalance } from 'components/commonGame/CommonCoinsBalance';
import { GemsBalance } from 'components/commonGame/GemsBalance';
import { EnergyDetailDrawer } from 'components/energy/EnergyDetailDrawer';
import { PageHeader } from 'components/navigation/PageHeader';
import { Avatar } from 'components/user/Avatar';
import {
  ANY_SPORT_MY_CARDS_ESSENCE_TYPE,
  ANY_SPORT_MY_CLUB_SLUG,
  ANY_SPORT_PLAY_INVENTORY_REVEALABLE_CARDS_TYPE,
} from 'constants/__generated__/routes';
import { useCurrentUserContext } from 'contexts/currentUser';
import { useSportContext } from 'contexts/sport';
import { useIsDesktopAndAbove } from 'hooks/device/useIsDesktopAndAbove';
import { useScreenSize } from 'hooks/device/useScreenSize';
import { useFeatureFlags } from 'hooks/useFeatureFlags';
import { useIsMobileApp } from 'hooks/useIsMobileApp';

const AvatarWrapper = styled.div``;

const Nickname = styled(LinkOverlay)`
  font: var(--t-bold) var(--t-label-m);
  ${ellipsis}
  &:hover {
    opacity: 0.8;
  }
`;

const Wrapper = styled(LinkBox)`
  display: flex;
  gap: var(--unit);
  align-items: center;
  width: fit-content;
  transition: opacity 0.2s ease-in-out;
  &:hover {
    ${AvatarWrapper}, & ${Nickname} {
      opacity: 0.8;
    }
  }
`;

type Props = {
  showCommonCoins?: Nullable<boolean>;
};
export const ProfilePageHeader = ({ showCommonCoins }: Props) => {
  const {
    flags: { useMlbOffseason = false, commonCardMonetization = false },
  } = useFeatureFlags();
  const { currentUser } = useCurrentUserContext();
  const { generateSportContextPath } = useSportContext();
  const { sport } = useSportContext();
  const isDesktopAndAbove = useIsDesktopAndAbove();
  const { up: isSEAndAbove } = useScreenSize('se');
  const [openEnergyBalanceHelp, setOpenEnergyBalanceHelp] = useState(false);
  const { isAndroidApp } = useIsMobileApp();

  if (!currentUser) {
    return null;
  }
  const {
    shardsChests,
    energyBalance,
    anyUserSportProfile,
    commonCardPoints,
    betaTesterFootballCommonCardsGame,
    gemsBalances,
  } = currentUser;

  const { blueprintCardsCount = 0 } = anyUserSportProfile || {};

  const totalEnergy = energyBalance.reduce(
    (acc, balance) => acc + balance.amount,
    0
  );

  const totalShards = shardsChests.reduce(
    (acc, chest) => acc + chest.cardShardsCount,
    0
  );

  const chestWithThresholdUnlocked = shardsChests.find(
    chest => chest.newThresholdUnlocked
  );

  const displayCoins =
    showCommonCoins &&
    betaTesterFootballCommonCardsGame &&
    sport === Sport.FOOTBALL;

  return (
    <PageHeader disableBackBrowser>
      <Wrapper>
        <AvatarWrapper>
          <Avatar user={currentUser} size={4.5} rounded />
        </AvatarWrapper>
        <Vertical gap={0.5}>
          <Nickname
            as={Link}
            to={generateSportContextPath(ANY_SPORT_MY_CLUB_SLUG, {
              params: {
                slug: currentUser.slug,
              },
            })}
          >
            {currentUser.nickname}
          </Nickname>

          {!isDesktopAndAbove && (
            <LinkOther as="div">
              <Horizontal>
                {!isDesktopAndAbove && displayCoins && (
                  <CommonCoinsBalance commonCardPoints={commonCardPoints} />
                )}
                {!isDesktopAndAbove &&
                  displayCoins &&
                  commonCardMonetization &&
                  !isAndroidApp && (
                    <GemsBalance balance={gemsBalances[0]?.amount || 0} />
                  )}
                {blueprintCardsCount > 0 && (
                  <Button
                    size="compact"
                    color="tertiary"
                    to={generateSportContextPath(
                      ANY_SPORT_PLAY_INVENTORY_REVEALABLE_CARDS_TYPE,
                      {
                        params: {
                          type: Rarity.limited,
                        },
                      }
                    )}
                  >
                    <Cards size={1.5} />
                    <FormattedNumber value={blueprintCardsCount} />
                  </Button>
                )}
                <Button
                  size="compact"
                  color="tertiary"
                  to={generateSportContextPath(
                    ANY_SPORT_MY_CARDS_ESSENCE_TYPE,
                    {
                      params: {
                        type:
                          chestWithThresholdUnlocked?.rarity || Rarity.limited,
                      },
                    }
                  )}
                >
                  <Badge variant="dot" invisible={!chestWithThresholdUnlocked}>
                    <Shards size={1.5} />
                  </Badge>
                  <FormattedNumber value={totalShards} />
                </Button>
                {sport === Sport.BASEBALL && !useMlbOffseason && (
                  <Button
                    size="compact"
                    color="tertiary"
                    onClick={() => setOpenEnergyBalanceHelp(true)}
                  >
                    <Energy size={1.5} />
                    <FormattedNumber value={totalEnergy} />
                  </Button>
                )}
                {isSEAndAbove && <RaffleTicketBalanceButton />}
              </Horizontal>
            </LinkOther>
          )}
        </Vertical>
        {isDesktopAndAbove && displayCoins && (
          <LinkOther as="div">
            <CommonCoinsBalance commonCardPoints={commonCardPoints} />
          </LinkOther>
        )}
        {isDesktopAndAbove &&
          displayCoins &&
          commonCardMonetization &&
          !isAndroidApp && (
            <LinkOther as="div">
              <GemsBalance balance={gemsBalances[0]?.amount || 0} />
            </LinkOther>
          )}
        <EnergyDetailDrawer
          open={openEnergyBalanceHelp}
          onClose={() => setOpenEnergyBalanceHelp(false)}
        />
      </Wrapper>
    </PageHeader>
  );
};
